import { CommonModule } from '@angular/common';
import { Component, computed, input, OnChanges, output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, min, PathKind, required, SchemaPath, SchemaPathTree, validate } from '@angular/forms/signals';
import { DeliveryWindowForm } from '@geofences/delivery-windows/delivery-window';
import { DeliveryWindowVM } from '@geofences/delivery-windows/delivery-window';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'app-delivery-window-form-dialog',
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    MessageModule,
    FloatLabelModule,
    DatePickerModule
  ],
  templateUrl: './delivery-window-form-dialog.html',
  styleUrl: './delivery-window-form-dialog.scss'
})
export class DeliveryWindowFormDialog implements OnChanges {
  readonly visible = input<boolean>(false);
  readonly mode = input<'create' | 'edit'>('create');
  readonly value = input<DeliveryWindowVM>();
  readonly loading = input<boolean>(false);

  readonly visibleChange = output<boolean>();
  readonly formSubmit = output<DeliveryWindowForm>();
  readonly cancel = output<void>();

  private readonly deliveryWindowFormModel = signal<DeliveryWindowForm>({
    startTime: this.parseTimeToDate('09:00 AM'),
    endTime: this.parseTimeToDate('11:00 AM'),
    maxDeliveriesPerDay: 0
  });

  protected readonly deliveryWindowForm = form(this.deliveryWindowFormModel, (form) => {
    required(form.startTime);
    required(form.endTime);
    required(form.maxDeliveriesPerDay);
    min(form.maxDeliveriesPerDay, 1);
    this.endAfterStartValidator(form.endTime, form);
  });

  readonly isSubmitting = signal<boolean>(false);
  readonly showLegacyDataWarning = signal<boolean>(false);

  readonly dialogTitle = computed(() => {
    return this.mode() === 'create' ? 'Create Delivery Window' : 'Edit Delivery Window';
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && changes['visible'].currentValue) {
      if (this.value() && this.mode() === 'edit') {
        this.populateForm();
      } else if (this.mode() === 'create') {
        this.resetForm();
      }
    }
  }

  submitDeliveryWindowForm() {
    if (this.deliveryWindowForm().valid()) {
      this.isSubmitting.set(true);

      const { startTime, endTime, maxDeliveriesPerDay } = this.deliveryWindowForm().value();

      const formValue: DeliveryWindowForm = {
        startTime,
        endTime,
        maxDeliveriesPerDay
      };

      this.formSubmit.emit(formValue);
      this.deliveryWindowForm().reset();
      this.closeDialog();
    }
  }

  cancelDeliveryWindowForm() {
    this.cancel.emit();
    this.closeDialog();
  }

  toggleVisibility(visible: boolean) {
    if (!visible) {
      this.cancelDeliveryWindowForm();
    }
  }

  timeFormatValidator(field: SchemaPath<string, 1, PathKind.Child>) {
    validate(field, ({ value }) => {
      if (!value) return null;

      const timePattern = /^(0[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/;
      if (!timePattern.test(value() as string)) {
        return { kind: 'invalidTimeFormat' };
      }
      return null;
    });
  }

  endAfterStartValidator(
    field: SchemaPath<string | Date, 1, PathKind.Child>,
    form: SchemaPathTree<DeliveryWindowForm, PathKind.Root>
  ) {
    validate(field, ({ valueOf }) => {
      const startTime = valueOf(form.startTime);
      const endTime = valueOf(form.endTime);

      if (!startTime || !endTime) return null;

      const startMinutes = this.timeToMinutes(startTime);
      const endMinutes = this.timeToMinutes(endTime);

      if (startMinutes === null || endMinutes === null) return null;

      if (endMinutes <= startMinutes) {
        return { kind: 'endAfterStart', message: 'End time must be after start time' };
      }
      return null;
    });
  }

  private timeToMinutes(time: Date | string): number | null {
    if (time instanceof Date) {
      return time.getHours() * 60 + time.getMinutes();
    }

    const match = time.trim().match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
    if (!match) return null;

    let [, h, m, meridiem] = match;
    let hours = parseInt(h, 10);
    const minutes = parseInt(m, 10);

    if (meridiem.toUpperCase() === 'PM' && hours < 12) hours += 12;
    if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  private closeDialog() {
    this.visibleChange.emit(false);
    this.isSubmitting.set(false);
  }

  private populateForm() {
    const value = this.value();
    if (value) {
      this.deliveryWindowFormModel.set({
        startTime: this.parseTimeToDate(value.startTime),
        endTime: this.parseTimeToDate(value.endTime),
        maxDeliveriesPerDay: value.maxDeliveriesPerDay
      });
    }
  }

  parseTimeToDate(timeStr: string): Date {
    const [time, meridian] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (meridian?.toUpperCase() === 'PM' && hours < 12) hours += 12;
    if (meridian?.toUpperCase() === 'AM' && hours === 12) hours = 0;

    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  private resetForm() {
    this.deliveryWindowFormModel.set({
      startTime: this.parseTimeToDate('09:00 AM'),
      endTime: this.parseTimeToDate('11:00 AM'),
      maxDeliveriesPerDay: 0
    });
  }
}
